import { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import DataTable from '../../../../../components/Datatable';
import Loading from '../../../../../components/Loading';
import MessageCustom from '../../../../../components/MessageCustom';
import ReactSelect from '../../../../../components/ReactSelect';
import { getSortKeys, setAdditionalStyles } from '../../../../../helpers/table';
import { getDomainsApi } from '../../../promptGenerator/query';
import { COLUMNS } from './keys';
import { query } from './query';
import { fetchGscMetricsByUrl } from './gscQuery';

const STORAGE_PREFIX = 'citations_vs_gsc_domain_';
/** Returns the hostname of a GSC property URL, preserving the `www.` prefix. */
function hostFromUrl(url) {
    if (!url) return '';
    let value = String(url)
        .trim()
        .replace(/^sc-domain:/, '');
    if (!/^https?:\/\//.test(value)) value = `https://${value}`;
    try {
        return new URL(value).hostname;
    } catch (err) {
        return String(url)
            .replace(/^https?:\/\//, '')
            .replace(/\/.*$/, '');
    }
}

/** Normalizes a possibly comma-separated / JSON-encoded value into a string list. */
function parseList(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean).map((item) => String(item).trim());
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.startsWith('[')) {
            try {
                return parseList(JSON.parse(trimmed));
            } catch (err) {
                // fall through to comma split
            }
        }
        return trimmed
            ? trimmed
                  .split(',')
                  .map((item) => item.trim())
                  .filter(Boolean)
            : [];
    }
    return [];
}

function averageOf(series) {
    if (!series?.length) return null;
    const sum = series.reduce((acc, point) => acc + (point.value ?? 0), 0);
    return sum / series.length;
}

/**
 * The citations report returns one row per (url, date). Group rows by url so each URL
 * becomes a single table entry with unioned platforms/prompts. Position, clicks and
 * impressions series are filled in later from the live GSC query (phase 2).
 */
function groupReportRows(results) {
    const byUrl = new Map();

    for (const row of results ?? []) {
        const url = row.url;
        if (!url) continue;

        if (!byUrl.has(url)) {
            byUrl.set(url, {
                url,
                title: row.title,
                platforms: new Set(),
                prompts: new Set(),
            });
        }

        const entry = byUrl.get(url);
        if ((!entry.title || entry.title === 'None') && row.title) entry.title = row.title;

        // Platform values never contain commas, so splitting is safe.
        parseList(row.platforms).forEach((platform) => entry.platforms.add(platform));
        // Prompts are free text (may contain commas) — keep each row's prompt whole.
        if (row.prompts && String(row.prompts).trim()) {
            entry.prompts.add(String(row.prompts).trim());
        }
    }

    return [...byUrl.values()].map((entry) => {
        const platformsList = [...entry.platforms];
        const promptsList = [...entry.prompts];

        return {
            id: uuid(),
            url: entry.url,
            title: entry.title,
            platformsList,
            platformsCount: platformsList.length,
            promptsList,
            promptsCount: promptsList.length,
            avgPosition: null,
            positionSeries: [],
            gscClicks: [],
            gscImpressions: [],
            gscClicksTotal: 0,
            gscImpressionsTotal: 0,
            gscLoaded: false,
        };
    });
}

/**
 * "Citations vs GSC" tab.
 *
 * Correlates the project's AI citation URLs (from the `ai-tracking-citations_vs_gsc`
 * BigQuery report, scoped to a user-selected GSC domain) with live Google Search
 * Console clicks/impressions for the exact same URLs.
 *
 * Flow:
 *   1. The GSC domain must be associated with the project first. The selection is
 *      persisted per-project in localStorage (no backend field involved).
 *   2. Phase 1 runs the citations report and renders the table immediately.
 *   3. Phase 2 pulls the unique URLs' GSC metrics via the live proxy endpoint and
 *      merges them into the rows while a secondary loader is shown in those columns.
 */
class CitationsVsGsc extends Component {
    constructor(props) {
        super(props);

        this.getReportData = this.getReportData.bind(this);
        this.getGscData = this.getGscData.bind(this);
        this.forceRun = this.forceRun.bind(this);
        this.onSelectDomain = this.onSelectDomain.bind(this);
        this.searchFunction = this.searchFunction.bind(this);

        this.state = {
            data: [],
            loading: false,
            gscLoading: false,
            domainOptions: [],
            selectedDomain: this.readStoredDomain(props.project?.id),
        };
    }

    storageKey(projectId = this.props.project?.id) {
        return `${STORAGE_PREFIX}${projectId}`;
    }

    readStoredDomain(projectId) {
        try {
            const raw = localStorage.getItem(this.storageKey(projectId));
            return raw ? JSON.parse(raw) : null;
        } catch (err) {
            return null;
        }
    }

    storeDomain(domain) {
        try {
            localStorage.setItem(this.storageKey(), JSON.stringify(domain));
        } catch (err) {
            // ignore quota / serialization issues
        }
    }

    async componentDidMount() {
        await this.loadDomainOptions();
        if (this.state.selectedDomain && this.props.prompt_uids?.length) {
            this.getReportData({});
        }
    }

    componentDidUpdate(prevProps) {
        const promptsChanged =
            JSON.stringify(prevProps.prompt_uids) !== JSON.stringify(this.props.prompt_uids);
        const platformsChanged =
            JSON.stringify(prevProps.platforms) !== JSON.stringify(this.props.platforms);

        if ((promptsChanged || platformsChanged) && this.state.selectedDomain) {
            this.getReportData({});
        }

        if (this.props.updateAll !== prevProps.updateAll && this.state.selectedDomain) {
            this.forceRun();
        }
    }

    async loadDomainOptions() {
        try {
            const res = await getDomainsApi();
            const domains = await res.json();
            const domainOptions = (domains ?? [])
                .filter((domain) => domain.active)
                .map((domain) => ({
                    value: domain.id,
                    id: domain.id,
                    url: domain.url,
                    label: domain.url,
                }));
            this.setState({ domainOptions });
        } catch (err) {
            console.log(err);
        }
    }

    onSelectDomain(option) {
        const selectedDomain = { id: option.id, url: option.url };
        this.storeDomain(selectedDomain);
        this.setState({ selectedDomain, data: [] }, () => {
            if (this.props.prompt_uids?.length) this.getReportData({});
        });
    }

    async getReportData({ force = false }) {
        const { selectedDomain } = this.state;
        if (!selectedDomain || !this.props.prompt_uids?.length) {
            this.setState({ data: [], loading: false });
            return;
        }

        try {
            this.setState({ loading: true });

            this.query = query({
                prompt_uids: this.props.prompt_uids,
                platforms: this.props.platforms,
                gsc_domain: hostFromUrl(selectedDomain.url),
            });

            const results = await this.query.update({ force, alreadyInQueue: force });

            const data = groupReportRows(results);

            this.setState({ data, loading: false });
            this.getGscData(data);
        } catch (err) {
            console.log(err);
            this.setState({ loading: false });
        }
    }

    async getGscData(rows) {
        const { selectedDomain } = this.state;
        const urls = [...new Set((rows ?? []).map((row) => row.url).filter(Boolean))];
        if (!urls.length || !selectedDomain) return;

        try {
            this.setState({ gscLoading: true });
            const byUrl = await fetchGscMetricsByUrl({
                domainId: selectedDomain.id,
                domainUrl: selectedDomain.url,
                urls,
            });

            const merged = rows.map((row) => {
                const metrics = byUrl[row.url] ?? byUrl[row.url?.replace(/\/$/, '')] ?? {};
                const position = metrics.position ?? [];
                const clicks = metrics.clicks ?? [];
                const impressions = metrics.impressions ?? [];
                return {
                    ...row,
                    positionSeries: position,
                    avgPosition: averageOf(position),
                    gscClicks: clicks,
                    gscImpressions: impressions,
                    gscClicksTotal: clicks.reduce((acc, point) => acc + (point.value ?? 0), 0),
                    gscImpressionsTotal: impressions.reduce(
                        (acc, point) => acc + (point.value ?? 0),
                        0,
                    ),
                    gscLoaded: true,
                };
            });

            this.setState({ data: merged, gscLoading: false });
        } catch (err) {
            console.log(err);
            // Mark rows as loaded so the loaders stop even when GSC fails.
            this.setState({
                data: rows.map((row) => ({ ...row, gscLoaded: true })),
                gscLoading: false,
            });
        }
    }

    forceRun() {
        this.getReportData({ force: true });
    }

    searchFunction(data, search) {
        if (!search) return data;
        const term = search.toLowerCase();
        return data.filter(
            (item) =>
                item.url?.toLowerCase().includes(term) || item.title?.toLowerCase().includes(term),
        );
    }

    render() {
        const { t } = this.props;
        const { selectedDomain, domainOptions, loading, gscLoading, data } = this.state;
        const selectedOption = domainOptions.find((option) => option.id === selectedDomain?.id);

        return (
            <div className="card rounded-main" style={{ minHeight: '500px' }}>
                <div className="card-body">
                    <div className="box-header">
                        {t('ai_tracking.keyword_detail.tabs.citations_vs_gsc')}
                    </div>
                    <div className="citations-gsc-domain-bar pb-0 w-100">
                        <label className="ai-tracking-create-label">
                            {t('ai_tracking.keyword_detail.citations_vs_gsc.select_domain')}{' '}
                            <span className="text-danger">*</span>
                        </label>
                        <ReactSelect
                            options={domainOptions}
                            value={selectedOption ?? null}
                            customeOnChange={this.onSelectDomain}
                            classname="mb-10 w-100"
                            placeholder={t(
                                'ai_tracking.keyword_detail.citations_vs_gsc.select_domain_placeholder',
                            )}
                            components={{ IndicatorSeparator: () => null }}
                        />
                    </div>

                    {!selectedDomain && (
                        <div className="card-body pt-0">
                            <MessageCustom
                                message={t(
                                    'ai_tracking.keyword_detail.citations_vs_gsc.select_domain_hint',
                                )}
                                message_type={'alert-warning'}
                            />
                        </div>
                    )}

                    {selectedDomain && loading && <Loading padding={10} />}

                    {selectedDomain && !loading && (
                        <>
                            {gscLoading && (
                                <div className="citations-gsc-secondary-loader card-body pt-0 d-flex align-items-center gap-8">
                                    <Loading onlyPreloader />
                                    <span>
                                        {t(
                                            'ai_tracking.keyword_detail.citations_vs_gsc.loading_gsc_data',
                                        )}
                                    </span>
                                </div>
                            )}
                            <DataTable
                                nodes={data ?? []}
                                sortFns={getSortKeys(COLUMNS(t))}
                                searchFunction={this.searchFunction}
                                COLUMNS={COLUMNS(t)}
                                // header={t('ai_tracking.keyword_detail.tabs.citations_vs_gsc')}
                                customEmptyMessage={t('global.no_data')}
                                additionalStyles={setAdditionalStyles(COLUMNS(t))}
                                forceRun={this.forceRun}
                                notUseDefaultStyle
                            />
                        </>
                    )}
                </div>
            </div>
        );
    }
}

export default withTranslation()(CitationsVsGsc);
